import { ApiError } from '../api/client';
import type { AsyncState } from '../screens/contracts';

/**
 * The slice of a TanStack query these helpers actually read.
 *
 * Structural rather than `UseQueryResult<T>`: that type's error parameter varies between
 * call sites, and matching it exactly would make every caller fight generic variance for
 * no benefit. Four fields is all this needs.
 */
interface QueryLike {
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  refetch: () => unknown;
}

/**
 * Flattens a TanStack query into the four fields a screen actually needs.
 *
 * Variants never see a query object, which keeps them from depending on the data layer and
 * means an error state is a plain string they can typeset however they like.
 */
export function asyncState(query: QueryLike): AsyncState {
  return {
    // `isPending` alone would flash a spinner on every background refetch; this only
    // reports loading when there is genuinely nothing to show yet.
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error ? messageFor(query.error) : null,
    retry: () => void query.refetch(),
  };
}

export function messageFor(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;
  return 'Something went wrong.';
}

/** Combines several queries into one state, reporting the first real problem. */
export function combineAsync(...queries: QueryLike[]): AsyncState {
  const failing = queries.find((q) => q.isError);
  return {
    isLoading: queries.some((q) => q.isLoading),
    isError: Boolean(failing),
    error: failing?.error ? messageFor(failing.error) : null,
    retry: () => {
      for (const query of queries) void query.refetch();
    },
  };
}
